import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { UnitProvider } from "@/context/UnitContext";
import Landing from "@/pages/Landing";
import Search from "@/pages/Search";
import ListingDetail from "@/pages/ListingDetail";
import ReservationSuccess from "@/pages/ReservationSuccess";
import ReservationPaymentSuccess from "@/pages/ReservationPaymentSuccess";
import OrderSuccess from "@/pages/OrderSuccess";
import ReservationPaymentCancel from "@/pages/ReservationPaymentCancel";
import BoroughLanding from "@/pages/BoroughLanding";
import BoroughCategoryLanding from "@/pages/BoroughCategoryLanding";
import Trade from "@/pages/Trade";
import Sustainability from "@/pages/Sustainability";
import Fitters from "@/pages/Fitters";
import FitterRegister from "@/pages/FitterRegister";
import FitterDashboard from "@/pages/FitterDashboard";
import WholeHome from "@/pages/WholeHome";
import RetailerLogin from "@/pages/RetailerLogin";
import RetailerStart from "@/pages/RetailerStart";
import RetailerDashboard from "@/pages/RetailerDashboard";
import RetailerPayouts from "@/pages/RetailerPayouts";
import RetailerNewListing from "@/pages/RetailerNewListing";
import RetailerListingEdit from "@/pages/RetailerListingEdit";
import RetailerReservations from "@/pages/RetailerReservations";
import RetailerEnquiries from "@/pages/RetailerEnquiries";
import AdminOverview from "@/pages/AdminOverview";
import AdminRetailerDetail from "@/pages/AdminRetailerDetail";
import AdminEmailPreview from "@/pages/AdminEmailPreview";
import AdminFitters from "@/pages/AdminFitters";
import AdminProjects from "@/pages/AdminProjects";
import AdminOrders from "@/pages/AdminOrders";
import AdminOpportunities from "@/pages/AdminOpportunities";
import ClaimAccount from "@/pages/ClaimAccount";
import AlertsUnsubscribe from "@/pages/AlertsUnsubscribe";
import CustomerAccount from "@/pages/CustomerAccount";
import AuthCallback from "@/pages/AuthCallback";
import ResetPassword from "@/pages/ResetPassword";
import VerifyEmail from "@/pages/VerifyEmail";

function ScrollManager() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const id = hash.slice(1);
      const scroll = () => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth" });
        else window.scrollTo(0, 0);
      };
      // Wait a tick for the target page to render before scrolling.
      const t = setTimeout(scroll, 60);
      return () => clearTimeout(t);
    }
    window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}

function Protected({ children, adminOnly, retailerOnly }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-8 text-slate-500">Loading…</div>;
  if (!user) return <Navigate to="/retailer/login" replace />;
  // Customers never belong on retailer/admin pages — send them to their account
  // (prevents the retailer<->admin redirect loop for non-trade roles).
  if ((adminOnly || retailerOnly) && user.role !== "admin" && user.role !== "retailer") {
    return <Navigate to="/account" replace />;
  }
  if (adminOnly && user.role !== "admin") return <Navigate to="/retailer" replace />;
  if (retailerOnly && user.role !== "retailer") return <Navigate to="/admin" replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <UnitProvider>
            <Toaster richColors position="top-right" />
            <ScrollManager />
            <AppRoutes />
          </UnitProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

function AppRoutes() {
  const location = useLocation();
  // CRITICAL (Emergent Google Auth): detect the one-time session_id in the URL
  // fragment synchronously during render — BEFORE any route/auth check — so the
  // callback processes first and we avoid the /auth/me race.
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/search" element={<Search />} />
            <Route path="/account" element={<CustomerAccount />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/trade" element={<Trade />} />
            <Route path="/sustainability" element={<Sustainability />} />
            <Route path="/fitters" element={<Fitters />} />
            <Route path="/fitters/join" element={<FitterRegister />} />
            <Route path="/fitter/dashboard" element={<FitterDashboard />} />
            <Route path="/whole-home" element={<WholeHome />} />
            <Route path="/listing/:id" element={<ListingDetail />} />
            <Route path="/reservation/success" element={<ReservationPaymentSuccess />} />
            <Route path="/order/success" element={<OrderSuccess />} />
            <Route path="/order/cancel" element={<ReservationPaymentCancel />} />
            <Route path="/reservation/cancel" element={<ReservationPaymentCancel />} />
            <Route path="/reservation/:id" element={<ReservationSuccess />} />
            <Route path="/flooring/:borough" element={<BoroughLanding />} />
            <Route path="/flooring/:borough/:category" element={<BoroughCategoryLanding />} />
            <Route path="/retailer/login" element={<RetailerLogin />} />
            <Route path="/retailer/register" element={<RetailerLogin />} />
            <Route path="/sell" element={<RetailerStart />} />
            <Route path="/claim/:token" element={<ClaimAccount />} />
            <Route path="/alerts/unsubscribe" element={<AlertsUnsubscribe />} />
            <Route
              path="/retailer"
              element={
                <Protected retailerOnly>
                  <RetailerDashboard />
                </Protected>
              }
            />
            <Route
              path="/retailer/new"
              element={
                <Protected retailerOnly>
                  <RetailerNewListing />
                </Protected>
              }
            />
            <Route
              path="/retailer/listings/:id/edit"
              element={
                <Protected retailerOnly>
                  <RetailerListingEdit />
                </Protected>
              }
            />
            <Route
              path="/retailer/reservations"
              element={
                <Protected retailerOnly>
                  <RetailerReservations />
                </Protected>
              }
            />
            <Route
              path="/retailer/payouts"
              element={
                <Protected retailerOnly>
                  <RetailerPayouts />
                </Protected>
              }
            />
            <Route
              path="/retailer/enquiries"
              element={
                <Protected retailerOnly>
                  <RetailerEnquiries />
                </Protected>
              }
            />
            <Route
              path="/admin"
              element={
                <Protected adminOnly>
                  <AdminOverview />
                </Protected>
              }
            />
            <Route
              path="/admin/retailers/:id"
              element={
                <Protected adminOnly>
                  <AdminRetailerDetail />
                </Protected>
              }
            />
            <Route
              path="/admin/emails"
              element={
                <Protected adminOnly>
                  <AdminEmailPreview />
                </Protected>
              }
            />
            <Route
              path="/admin/fitters"
              element={
                <Protected adminOnly>
                  <AdminFitters />
                </Protected>
              }
            />
            <Route
              path="/admin/projects"
              element={
                <Protected adminOnly>
                  <AdminProjects />
                </Protected>
              }
            />
            <Route
              path="/admin/orders"
              element={
                <Protected adminOnly>
                  <AdminOrders />
                </Protected>
              }
            />
            <Route
              path="/admin/opportunities"
              element={
                <Protected adminOnly>
                  <AdminOpportunities />
                </Protected>
              }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
  );
}

export default App;
